import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  BookOpen,
  Brain,
  Target,
  Clock,
  CheckCircle,
  GraduationCap,
  Users,
  Award,
  ArrowRight,
  Mail,
  FileText,
  Play,
  Home,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_academia-sabeneval/artifacts/ai0i1vi4_2F29CCC3-E812-45AF-8948-674464C4314C.PNG";

const MODULE_ICONS = {
  1: BookOpen,
  2: Brain,
  3: BookOpen,
  4: Target,
  5: Clock,
  6: Play,
  7: GraduationCap
};

export default function CoursePage() {
  const navigate = useNavigate();
  const [courseInfo, setCourseInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourseInfo = async () => {
      try {
        const response = await axios.get(`${API}/course-info`);
        setCourseInfo(response.data);
      } catch (error) {
        console.error("Error fetching course info:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourseInfo();
  }, []);

  const features = [
    { icon: BookOpen, text: "7 módulos de estudio completos" },
    { icon: Play, text: "3 simuladores tipo EXANI II" },
    { icon: Clock, text: "Acceso ilimitado al material" },
    { icon: Users, text: "Contenido actualizado según CENEVAL" },
    { icon: Award, text: "Estrategias probadas para el examen" },
    { icon: Target, text: "Enfocado en el área de la salud" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <a href="/" data-testid="logo-link" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
            <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
          </a>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/")}
              variant="ghost"
              className="text-slate-600 hidden sm:flex"
              data-testid="home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Inicio
            </Button>
            <Button
              onClick={() => navigate("/inscripcion")}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
              data-testid="header-enroll-btn"
            >
              Inscribirse
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <GraduationCap className="w-4 h-4" />
                Programa Completo
              </div>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
                {courseInfo?.title || "Curso Completo de Preparación EXANI II"}
              </h1>

              <p className="text-lg text-slate-600 mb-6">
                {courseInfo?.description || "Programa integral diseñado para estudiantes del área de la salud."}
              </p>

              <div className="flex items-baseline gap-2 mb-8">
                <span className="text-5xl font-bold text-purple-600">
                  ${courseInfo?.price?.toLocaleString() || "2,999"}
                </span>
                <span className="text-xl text-slate-500">MXN</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate("/inscripcion")}
                  className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-12 px-8 text-base font-medium"
                  data-testid="hero-enroll-btn"
                >
                  Inscribirse al Curso
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate("/examen-diagnostico")}
                  variant="outline"
                  className="rounded-full h-12 px-8 text-base font-medium border-slate-300"
                  data-testid="hero-diagnostic-btn"
                >
                  Hacer Diagnóstico Primero
                </Button>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1766297247913-54717c00e79c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Laboratorio"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 rounded-xl bg-slate-50"
                data-testid={`feature-${index}`}
              >
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-slate-700">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Modules */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Estructura del Curso
            </h2>
            <p className="text-lg text-slate-600">
              7 módulos diseñados para una preparación completa
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4" data-testid="modules-accordion">
            {(courseInfo?.modules || []).map((module, index) => {
              const Icon = MODULE_ICONS[module.id] || BookOpen;
              return (
                <AccordionItem
                  key={module.id}
                  value={`module-${module.id}`}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                  data-testid={`module-${index}`}
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Icon className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm text-purple-600 font-medium">Módulo {module.id}</p>
                        <p className="text-lg font-semibold text-slate-900">{module.title}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="pl-16">
                      <p className="text-slate-600 mb-4">{module.description}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <FileText className="w-4 h-4" />
                        <span>Contenido disponible al inscribirse</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* Enrollment CTA */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-purple-600 to-purple-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Listo para comenzar tu preparación?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Inscríbete hoy y accede a todo el contenido del curso
          </p>

          <div className="bg-white/10 backdrop-blur rounded-2xl p-8 max-w-lg mx-auto mb-8">
            <div className="flex items-baseline justify-center gap-2 mb-4">
              <span className="text-5xl font-bold text-white">$2,999</span>
              <span className="text-xl text-purple-200">MXN</span>
            </div>
            <p className="text-purple-200 mb-6">Pago único • Acceso ilimitado</p>

            <Button
              onClick={() => navigate("/inscripcion")}
              className="bg-white text-purple-700 hover:bg-purple-50 rounded-full h-14 px-10 text-lg font-medium w-full sm:w-auto"
              data-testid="cta-enroll-btn"
            >
              Inscribirse al Curso
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          <div className="flex items-center justify-center gap-2 text-purple-200">
            <Mail className="w-4 h-4" />
            <span>¿Dudas? Contáctanos: sabeneval@gmail.com</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-purple-900 text-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
            <span className="font-heading font-bold text-xl text-white">SABENEVAL</span>
          </div>
          <p className="text-purple-300 text-sm">
            © 2024 Sabeneval. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
